import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { coalesce, isNullOrWhitespace } from '@asamco/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilKeyChanged, filter, first, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { MessageBusService, MessageBusType } from '../message-bus/message-bus.service';
import { AsamcoAuthService } from './asamco-auth.service';
import { Permissions } from './permissions';

interface ScopePermissions {
	[scopeKey: string]: string[];
}

/**
 * Injectable service for user module handling
 */
@Injectable({
	providedIn: 'root',
	deps: [HttpClient, AsamcoAuthService, MessageBusService],
})
export class AsamcoUserService {
	/** Gets the permissions for the authenticated user */
	private permissions$;

	/** Gets whether the user is superadmin */
	private isSuperAdmin$: Promise<boolean>;

	/**
	 * Permissions per scope, note that the key is ALWAYS mapped to lowercase
	 * I.e. * Key = Debtor:1, value = string[] (permissions guids)
	 */
	private scopePermissions$: Observable<ScopePermissions>;

	/**
	 * Constructor
	 */
	constructor(private http: HttpClient, private auth: AsamcoAuthService, private msgBus: MessageBusService) {
		msgBus
			.on<any>(MessageBusType.ConnectedApp, 'switched')
			.pipe(
				tap(_ => {
					this.reload();
				})
			)
			.subscribe({error: console.error});

		this.resetScopedPermissions();
	}

	/** Reset scope permissions (let the permission check wait for the permissions from the backend instead of using previous or empty value) */
	private resetScopedPermissions() {
		this.scopePermissions$ = combineLatest([this.auth.isLoggedIn$, this.auth.userScope$]).pipe(
			map(([isLoggedIn, userScopes]) => ({ isLoggedIn, userScopes })),
			distinctUntilKeyChanged('userScopes'),
			switchMap(data => {
				if (data.isLoggedIn) {
					return this.http.get<string[]>(`Permission/AuthenticatedUserScopePermissions`).pipe(
						catchError(() => of([])),
						map(permissions => {
							return Object.keys(permissions).reduce((pv, cv) => {
								pv[cv] = permissions[cv.toLocaleLowerCase()].map(p => p.toLocaleLowerCase());
								return pv;
							}, <ScopePermissions>{});
						})
					);
				} else {
					return of({});
				}
			}),
			shareReplay(1)
		);
	}

	public reload() {
		this.permissions$ = undefined;
		this.isSuperAdmin$ = undefined;
		this.resetScopedPermissions();
	}

	async getPermissions(): Promise<string[]> {
		if (this.permissions$ === undefined) {
			this.permissions$ = this.auth.isLoggedIn$
				.pipe(
					filter(Boolean),
					switchMap(() => this.http.get<string[]>(`Permission/AuthenticatedUserPermissions`)),
					catchError(() => of<string[]>([])),
					map(permissions => permissions.map(p => p.toLocaleLowerCase())),
					first()
				)
				.toPromise();
		}

		return this.permissions$;
	}

	private getIsSuperAdmin(): Promise<boolean> {
		if (this.isSuperAdmin$ === undefined) {
			this.isSuperAdmin$ = this.auth.isLoggedIn$
				.pipe(
					filter(Boolean),
					switchMap(() => this.http.get<boolean>(`User/IsSuperAdmin`)),
					catchError(() => of(false)),
					first()
				)
				.toPromise();
		}

		return this.isSuperAdmin$;
	}

	/** helper function to get if an user is an superAdmin */
	async isAdmin() {
		const isSuperAdmin = await this.getIsSuperAdmin();
		return this.auth.userSession && isSuperAdmin;
	}

	/**
	 * Checks if the given permission exists
	 * @param permissionId can be the name OR GUID from the permission
	 */
	private doesPermissionExists(permissionId: string) {
		return Permissions[permissionId] != null || (permissionId.length === 36 && Object.values(Permissions).includes(permissionId));
	}

	/**
	 * Checks whether the user has the given permission or not
	 */
	async hasPermission(permissionId: string, scope: string = null, scopeObjectId?: number): Promise<boolean> {
		// first check if the permission is known.
		if (isNullOrWhitespace(scope) && !this.doesPermissionExists(permissionId)) {
			// Permission has to be in the 'permissions.ts' file.
			console.error(`Trying to check a permission that doesn't exists '${permissionId}'. Access is denied`);
			return false;
		}
		// superUser check. do this first, no need to get the permissions from the sever then.
		if (await this.isAdmin()) {
			return true;
		}
		// fetch the permissions (the promise will keep it for the lifetime of the app)
		const permissionGuid = (Permissions[permissionId] || permissionId).toLowerCase();
		let userPermissions = [];
		if (isNullOrWhitespace(scope)) {
			userPermissions = await this.getPermissions();

			const hasPermission = userPermissions.includes(permissionGuid);
			return hasPermission;
		} else {
			return this.scopePermissions$
				.pipe(
					take(1),
					map(userScopePermissions => {
						const objectId = coalesce(scopeObjectId, this.auth.getUserScopeValueOrDefault(scope));
						const scopeKey = `${scope}:${objectId}`.toLocaleLowerCase();

						if (userScopePermissions[scopeKey] != null && Array.isArray(userScopePermissions[scopeKey])) {
							userPermissions = userScopePermissions[scopeKey];
						}
						const hasPermission = userPermissions.includes(permissionGuid);
						return hasPermission;
					})
				)
				.toPromise();
		}
	}

	/** Checks whether the user has the assigned permission without applying the superadmin shortcut. */
	async hasAssignedPermission(permissionId: string): Promise<boolean> {
		if (!this.doesPermissionExists(permissionId)) {
			console.error(`Trying to check a permission that doesn't exists '${permissionId}'. Access is denied`);
			return false;
		}

		const permissionGuid = (Permissions[permissionId] || permissionId).toLowerCase();
		const userPermissions = await this.getPermissions();
		return userPermissions.includes(permissionGuid);
	}
}
